/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HTTPClientService {
  public apiUrl = environment.apiBaseUrl + '/api';
  public aiApiUrl = environment.aiApiUrl;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  private getToken(): string | null {
    return this.cookieService.get('auth_token');
  }

  private getHeaders(headers?: HttpHeaders, exclude = false): HttpHeaders {
    if (!headers) {
      headers = new HttpHeaders();
    }
    headers = !exclude
      ? headers.set('Content-Type', 'application/json')
      : headers.set('Accept', '*/*');

    const token = this.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private handleError(error: any, state: any): Observable<never> {
    if (error.status === 403) {
      localStorage.setItem('path', state.url);
      // Redirect to login page if 403 error occurs
      localStorage.removeItem('user');
      this.router.navigate(['/home']);
    }
    return throwError(() => error);
  }

  get<T>(
    endpoint: string,
    params?: HttpParams,
    headers?: HttpHeaders,
  ): Observable<T> {
    headers = this.getHeaders(headers);
    console.log('HTTPClientService GET:', endpoint, 'PARAMS:', params?.toString());
    return this.http
      .get<T>(`${endpoint}`, { params, headers })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  post<T>(
    endpoint: string,
    body: any,
    headers?: HttpHeaders,
    params?: HttpParams,
  ): Observable<T> {
    headers = this.getHeaders(headers);
    return this.http
      .post<T>(`${endpoint}`, body, { headers, params })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }
  postForDownload(
    endpoint: string,
    body: any,
    responseType: 'blob' | 'text' = 'text',
    headers?: HttpHeaders,
    params?: HttpParams,
  ): Observable<any> {
    headers = this.getHeaders(headers);

    return this.http
      .post(`${endpoint}`, body, {
        headers,
        params,
        responseType: responseType as any,
      })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  postFormData<T>(
    endpoint: string,
    formData: FormData,
    headers?: HttpHeaders,
    params?: HttpParams,
  ): Observable<T> {
    if (!headers) {
      headers = new HttpHeaders();
    }
    const token = this.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http
      .post<T>(`${endpoint}`, formData, { headers, params })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  putFormData<T>(
    endpoint: string,
    formData: FormData,
    headers?: HttpHeaders,
    params?: HttpParams,
  ): Observable<T> {
    if (!headers) {
      headers = new HttpHeaders();
    }
    const token = this.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http
      .put<T>(`${endpoint}`, formData, { headers, params })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  post2<T>(
    endpoint: string,
    body: any,
    headers?: HttpHeaders,
    useAi = false,
    params?: HttpParams,
  ): Observable<T> {
    headers = this.getHeaders(headers);
    const url = useAi ? this.aiApiUrl : this.apiUrl;

    return this.http
      .post<T>(`${url}/${endpoint}`, body, {
        headers,
        params,
        withCredentials: true,
      })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  download<T>(endpoint: string, headers?: HttpHeaders): Observable<T> {
    headers = this.getHeaders(headers, true);
    return this.http
      .get<T>(`${endpoint}`, { headers })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  postFill<T>(
    endpoint: string,
    body: any,
    headers?: HttpHeaders,
  ): Observable<T> {
    headers = this.getHeaders(headers, true);
    return this.http
      .post<T>(`${endpoint}`, body, { headers })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  putFill<T>(
    endpoint: string,
    body: any,
    headers?: HttpHeaders,
    useAi = false,
  ): Observable<T> {
    headers = this.getHeaders(headers, true);
    const url = useAi ? this.aiApiUrl : this.apiUrl;
    return this.http
      .put<T>(`${url}/${endpoint}`, body, { headers })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  post3<T>(
    endpoint: string,
    body: any,
    headers?: HttpHeaders,
    useAi = false,
    params?: HttpParams,
    responseType = false,
  ): Observable<T> {
    headers = this.getHeaders(headers);
    const url = useAi ? this.aiApiUrl : this.apiUrl;

    if (responseType) {
      return this.http
        .post<T>(`${url}/${endpoint}`, body, {
          headers,
          params,
          responseType: 'blob' as 'json',
        })
        .pipe(
          catchError((error) =>
            this.handleError(error, this.router.routerState.snapshot),
          ),
        );
    }

    return this.http
      .post<T>(`${url}/${endpoint}`, body, { headers, params })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  patchFill<T>(
    endpoint: string,
    body: any,
    headers?: HttpHeaders,
  ): Observable<T> {
    headers = this.getHeaders(headers, true);
    return this.http
      .patch<T>(`${endpoint}`, body, { headers })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

  put<T>(
    endpoint: string,
    body: any = null,
    headers?: HttpHeaders,
    params?: HttpParams,
  ): Observable<T> {
    headers = this.getHeaders(headers);
    return this.http
      .put<T>(`${endpoint}`, body, { headers, params })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }

delete<T>(
  endpoint: string,
  params?: HttpParams,
  headers?: HttpHeaders,
): Observable<T> {
  headers = this.getHeaders(headers);
  return this.http
    .delete<T>(`${endpoint}`, { params, headers })
    .pipe(
      catchError((error) =>
        this.handleError(error, this.router.routerState.snapshot)
      )
    );
}

  patch<T>(
    endpoint: string,
    body: any,
    headers?: HttpHeaders,
  ): Observable<T> {
    headers = this.getHeaders(headers);
    return this.http
      .patch<T>(`${endpoint}`, body, { headers })
      .pipe(
        catchError((error) =>
          this.handleError(error, this.router.routerState.snapshot),
        ),
      );
  }
}
